import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FileText, MessageSquare, BarChart3, FolderOpen, LogOut, Menu, Users, Building2, Settings as SettingsIcon, Globe, ChevronDown, Shield, Mail, Bell, X, Check, ShieldCheck, KeyRound, User as UserIcon, Sparkles, LifeBuoy, Newspaper, Map as MapIcon, FileEdit, Bot } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../App';
import { toast } from 'sonner';
import TwoFactorSetup from './TwoFactorSetup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const Layout = ({ children, user, setUser }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [systemSettings, setSystemSettings] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [show2fa, setShow2fa] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userProfile, setUserProfile] = useState(user);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    fetchWorkspaces();
    fetchSystemSettings();
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close profile menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/notifications?limit=20`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.link) navigate(notification.link);
    setShowNotifications(false);
  };

  const fetchSystemSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/system/settings`);
      setSystemSettings(res.data);
    } catch (error) {
      console.error('Error fetching system settings:', error);
    }
  };

  const fetchWorkspaces = async () => {
    try {
      const res = await axios.get(`${API_URL}/auth/workspaces`);
      setWorkspaces(res.data.workspaces || []);
      try {
        const wsRes = await axios.get(`${API_URL}/workspaces/my`);
        setCurrentWorkspace(wsRes.data);
      } catch (e) {}
    } catch (error) {
      console.error('Error fetching workspaces:', error);
    }
  };

  const handleWorkspaceSwitch = async (workspaceId) => {
    try {
      const res = await axios.post(`${API_URL}/auth/switch-workspace/${workspaceId}`);
      localStorage.setItem('token', res.data.access_token);
      setCurrentWorkspace(res.data.workspace);
      setShowWorkspaceMenu(false);
      window.location.reload();
    } catch (error) {
      console.error('Error switching workspace:', error);
    }
  };

  const primaryColor = systemSettings?.primary_color || '#7c3aed';
  const appName = systemSettings?.app_name || 'DocuBot';
  const logoUrl = systemSettings?.logo_url;

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/auth');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const navItems = [
    { icon: FileText, label: t('dashboard'), path: '/' },
    { icon: LifeBuoy, label: t('documents'), path: '/documents' },
    { icon: Sparkles, label: t('aiStudio') || 'AI Studio', path: '/ai-studio', accent: true },
    { icon: Bot, label: 'Chatbot', path: '/chatbot-settings' },
    { icon: FileText, label: 'Flow Builder', path: '/chatbot-builder' },
    { icon: Newspaper, label: 'News', path: '/news' },
    { icon: MapIcon, label: 'Roadmap', path: '/roadmap' },
    { icon: FileEdit, label: 'Forms', path: '/forms' },
    { icon: BarChart3, label: t('analytics'), path: '/analytics' },
  ];

  if (user.role === 'workspace_admin' || user.is_super_admin) {
    navItems.push({ icon: Mail, label: 'Contact Requests', path: '/contact-requests' });
    navItems.push({ icon: SettingsIcon, label: t('settings'), path: '/settings' });
  }

  if (user.is_super_admin) {
    navItems.push({ icon: Shield, label: t('superAdminPanel'), path: '/admin' });
  }

  return (
    <div className="flex min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-zinc-200 flex flex-col" data-testid="sidebar">
        <div className="p-6 border-b border-zinc-200">
          <div className="flex items-center gap-2 mb-4">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-10" />
            ) : (
              <>
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <span className="text-xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{appName}</span>
              </>
            )}
          </div>
          <button
            onClick={toggleLanguage}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 transition-colors text-sm font-medium"
            data-testid="language-toggle"
          >
            <Globe className="h-4 w-4" />
            <span>{i18n.language === 'en' ? 'Français' : 'English'}</span>
          </button>
        </div>

        {/* Workspace Switcher */}
        {workspaces.length > 0 && (
          <div className="px-4 py-3 border-b border-zinc-200">
            <div className="relative">
              <button
                onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-md bg-violet-50 hover:bg-violet-100 transition-colors"
                data-testid="workspace-switcher"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Building2 className="h-4 w-4 text-violet-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-violet-900 truncate">
                    {currentWorkspace?.name || t('allWorkspaces')}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 text-violet-600" />
              </button>
              {showWorkspaceMenu && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-zinc-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                  {workspaces.map((ws) => (
                    <button
                      key={ws.id}
                      onClick={() => handleWorkspaceSwitch(ws.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0 ${
                        currentWorkspace?.id === ws.id ? 'bg-violet-50' : ''
                      }`}
                      data-testid={`workspace-${ws.id}`}
                    >
                      <div className="font-medium text-sm text-zinc-900">{ws.name}</div>
                      <div className="text-xs text-zinc-500">{ws.subdomain}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <nav className="flex-1 p-4 overflow-y-auto" data-testid="navigation">
          <div className="space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  location.pathname === item.path
                    ? item.accent
                      ? 'bg-gradient-to-r from-violet-100 to-fuchsia-100 text-violet-700 font-semibold'
                      : 'bg-violet-100 text-violet-600 font-semibold'
                    : item.accent
                      ? 'text-zinc-700 hover:bg-gradient-to-r hover:from-violet-50 hover:to-fuchsia-50 hover:text-violet-600'
                      : 'text-zinc-700 hover:bg-violet-50 hover:text-violet-600'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className={`h-5 w-5 ${item.accent ? 'text-fuchsia-500' : ''}`} />
                <span className="font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>{item.label}</span>
                {item.accent && (
                  <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white rounded">
                    New
                  </span>
                )}
              </Link>
            ))}
          </div>
        </nav>
      </aside>

      {/* Main Content Area with Header */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-14 bg-white border-b border-zinc-200 flex items-center justify-end px-6 flex-shrink-0 gap-2">
          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => { setShowNotifications(!showNotifications); setShowProfileMenu(false); }}
              className="relative p-2 rounded-full hover:bg-zinc-100 transition-colors"
              data-testid="notification-bell"
            >
              <Bell className="h-5 w-5 text-zinc-600" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 max-h-[70vh] overflow-hidden">
                <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
                  <h3 className="font-semibold text-zinc-900">Notifications</h3>
                  {unreadCount > 0 && (
                    <button onClick={markAllAsRead} className="text-xs text-violet-600 hover:text-violet-700 font-medium">
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto max-h-[50vh]">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
                      <p className="text-zinc-500 text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={`p-4 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${
                          !notification.read ? 'bg-violet-50' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`h-2 w-2 rounded-full mt-2 flex-shrink-0 ${!notification.read ? 'bg-violet-600' : 'bg-transparent'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-zinc-900 truncate">{notification.title}</p>
                            <p className="text-xs text-zinc-600 mt-1 line-clamp-2">{notification.message}</p>
                            <p className="text-xs text-zinc-400 mt-2">
                              {new Date(notification.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile Dropdown */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={() => { setShowProfileMenu(!showProfileMenu); setShowNotifications(false); }}
              className="flex items-center gap-2 p-1.5 rounded-full hover:bg-zinc-100 transition-colors"
              data-testid="profile-menu-button"
            >
              <div className="h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center">
                <span className="text-violet-600 font-semibold text-sm">{user.name.charAt(0)}</span>
              </div>
              <ChevronDown className="h-3.5 w-3.5 text-zinc-500" />
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-zinc-200 rounded-lg shadow-xl z-50 overflow-hidden">
                {/* User Info */}
                <div className="px-4 py-3 border-b border-zinc-100">
                  <div className="font-semibold text-sm text-zinc-900 truncate">{user.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{user.email}</div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <button
                    onClick={() => { setShowPasswordDialog(true); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors"
                    data-testid="change-password-button"
                  >
                    <KeyRound className="h-4 w-4 text-zinc-400" />
                    Change Password
                  </button>
                  <button
                    onClick={() => { setShow2fa(true); setShowProfileMenu(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors"
                    data-testid="security-2fa-button"
                  >
                    <ShieldCheck className="h-4 w-4 text-zinc-400" />
                    Security (2FA)
                  </button>
                </div>

                {/* Logout */}
                <div className="border-t border-zinc-100 py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    data-testid="logout-button"
                  >
                    <LogOut className="h-4 w-4" />
                    {t('logout')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* 2FA Dialog */}
      <TwoFactorSetup
        open={show2fa}
        onOpenChange={setShow2fa}
        user={userProfile}
        onUpdate={() => {
          axios.get(`${API_URL}/auth/me`).then(res => setUserProfile(res.data)).catch(() => {});
        }}
      />

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={showPasswordDialog}
        onOpenChange={setShowPasswordDialog}
      />
    </div>
  );
};

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-violet-600" />
            Change Password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="current-pw">Current Password</Label>
            <Input
              id="current-pw"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="mt-1.5"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <Label htmlFor="new-pw">New Password</Label>
            <Input
              id="new-pw"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="mt-1.5"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pw">Confirm New Password</Label>
            <Input
              id="confirm-pw"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="mt-1.5"
              data-testid="confirm-password-input"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={loading} data-testid="save-password-button">
              {loading ? 'Saving...' : 'Update Password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Layout;
